// The two dashboard cards that read from outside the portrait: Threads and "While you were away".
//
// Kept apart from usePortrait because neither is part of the portrait and both are allowed to fail
// quietly. A dashboard is a glance, not a report: if either read fails the card is simply absent,
// which is also what a new account legitimately looks like.

import { useEffect, useMemo, useState } from 'react';
import { useSession } from '../auth/SessionProvider';
import { useEntries } from '../entries/EntriesProvider';
import { fetchLinks, resolveLinks, type EntryLink, type ResolvedLink } from '../api/links';
import { fetchPortraitChanges, type PortraitChanges } from '../api/portrait';

export interface HomeSignals {
  /** Links whose ends can both be named — a loaded entry, or a theme, newest first. */
  threads: ResolvedLink[];
  changes: PortraitChanges | null;
}

export function useHomeSignals(): HomeSignals {
  const { vault } = useSession();
  const dek = vault.status === 'unlocked' ? vault.dek : null;
  const { entries } = useEntries();

  const [links, setLinks] = useState<EntryLink[]>([]);
  const [changes, setChanges] = useState<PortraitChanges | null>(null);

  useEffect(() => {
    if (!dek) return;
    const controller = new AbortController();

    (async () => {
      const [linkResult, changeResult] = await Promise.allSettled([
        fetchLinks(dek, { signal: controller.signal }),
        // peek: rendering the dashboard must not count as having looked at the portrait, or the
        // card would empty itself on the next refresh and take the Self screen's badge with it.
        fetchPortraitChanges({ peek: true }, controller.signal),
      ]);

      if (controller.signal.aborted) return;
      if (linkResult.status === 'fulfilled') setLinks(linkResult.value);
      if (changeResult.status === 'fulfilled') setChanges(changeResult.value);
    })();

    return () => controller.abort();
  }, [dek]);

  // Links arrive as entry ids; the titles live in the entries the provider already decrypted.
  const threads = useMemo(() => resolveLinks(links, entries), [links, entries]);

  return { threads, changes };
}
